import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { ProfileModel } from '@pukka/api';
import { Button, Icon, Input, Menu } from '@pukka/ui-components';
import { DefaultActionBar, OutletSelector } from '@pukka/ui-composite';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio';

import { BrandsPersistedState, BrandsState } from '../Brands.state';
import { BrandsFormState } from '../BrandsForm/BrandsForm.state';

function SearchCombobox({
  query,
  submittedQuery,
  placeholder,
  onSubmit,
  onChange,
  onChangeSubmitted,
}: {
  query: string;
  submittedQuery: string;
  placeholder: string;
  onSubmit: (value: { query: string; type: 'brand' | 'parent' }) => void;
  onChange: (value: string) => void;
  onChangeSubmitted: (value: string) => void;
}) {
  const { t } = useTranslation('library');

  const handleSubmitSearch = (type: 'brand' | 'parent') => {
    if (type) {
      onSubmit({
        query,
        type,
      });

      onChangeSubmitted(query);
    }
  };

  const handleClearInput = () => {
    onChange('');
    onChangeSubmitted('');
    onSubmit({
      query: '',
      type: 'parent',
    });

    const searchInput = window.document?.getElementById('list-search') as HTMLInputElement | null;

    /* istanbul ignore next */
    if (searchInput) {
      searchInput.value = '';
    }
  };

  const handleBlur = () => {
    onChange(submittedQuery);
  };

  return (
    <Combobox value={submittedQuery} onChange={handleSubmitSearch}>
      <ComboboxInput as={Fragment}>
        <div className="relative border-none p-0">
          <Input
            id="list-search"
            placeholder={placeholder}
            className="w-72"
            type="text"
            icon="search"
            reverse
            autoComplete="off"
            value={query}
            onBlur={handleBlur}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />

          {submittedQuery && (
            <div className="absolute right-[6px] top-[6px] bg-white pl-1">
              <Icon
                icon="wrong"
                size="sm"
                data-testid="clear-search"
                className="cursor-pointer text-neutral-400 hover:text-neutral-600"
                onClick={handleClearInput}
              />
            </div>
          )}
        </div>
      </ComboboxInput>
      <ComboboxOptions
        anchor={{ to: 'bottom', gap: 4 }}
        className={twMerge('w-72 rounded-md border bg-neutral-50 p-2 shadow-md empty:invisible', !query && 'invisible')}
      >
        {['brand', 'parent'].map((type) => {
          return (
            <ComboboxOption as={Fragment} key={type} value={type}>
              {/* eslint-disable-next-line react/no-unused-prop-types */}
              {({ focus }: { focus: boolean }) => (
                <div className={twMerge('group flex justify-between gap-2 rounded-md px-4 py-2', focus && 'bg-blue-100')}>
                  <div className="max-w-36 truncate">{query}</div>
                  <span className="text-blue-500">{t(`brands.searchIn.${type}`)}</span>
                </div>
              )}
            </ComboboxOption>
          );
        })}
      </ComboboxOptions>
    </Combobox>
  );
}

function BrandsActionBar({
  state = BrandsState,
  persistedState = BrandsPersistedState,
  formState = BrandsFormState,
}: {
  state?: typeof BrandsState;
  persistedState?: typeof BrandsPersistedState;
  formState?: typeof BrandsFormState;
}) {
  const { t } = useTranslation('library');

  const { outlets } = useSnapshot(persistedState) as typeof BrandsPersistedState;
  const { selectedOutlet } = useSnapshot(state) as typeof BrandsState;
  const { useBrands } = state;
  const { handleClickCreateParent, handleClickCreateEmptyBrand } = formState;

  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');

  useBrands();

  const handleSubmitSearch = (value: { query: string; type: 'brand' | 'parent' }) => {
    state.handleChangeKeyword(value.query, value.type);
  };

  const handleChangeOutlet = (outlet: ProfileModel.Outlet) => {
    state.handleChangeOutlet(outlet);

    setQuery('');
    setSubmittedQuery('');
  };

  return (
    <DefaultActionBar
      pageTitle={t('brands.pageTitle')}
      buttons={[
        <Menu
          align="end"
          trigger={
            <Button icon="chevron-down" reverse size="m" className="w-32">
              <div className="w-full">{t('brands.createButton')}</div>
            </Button>
          }
        >
          <Menu.Option className="mt-2" onClick={handleClickCreateParent}>
            {t('brands.createParentButton')}
          </Menu.Option>
          <Menu.Option className="mb-2" onClick={handleClickCreateEmptyBrand}>
            {t('brands.createBrandButton')}
          </Menu.Option>
        </Menu>,
      ]}
    >
      <div className="mb-8 flex gap-2">
        <OutletSelector
          disabled={!selectedOutlet.value}
          selectedOutlet={{
            ...selectedOutlet,
            meta: {
              ...selectedOutlet,
            },
          }}
          outlets={outlets}
          onOutletChange={handleChangeOutlet}
        />

        <SearchCombobox
          query={query}
          submittedQuery={submittedQuery}
          onChange={setQuery}
          onChangeSubmitted={setSubmittedQuery}
          placeholder={t('brands.searchBrandPlaceholder')}
          onSubmit={handleSubmitSearch}
        />
      </div>
    </DefaultActionBar>
  );
}

export { BrandsActionBar };
